/**
 * Keeps the re-fetchable data cache inside a byte budget so it can never fill
 * the ~5 MB localStorage origin quota (ORGIGGLY-WEB-11).
 *
 * Before this, every org a browser visited left its published config and data
 * batches in localStorage forever. Once the origin filled, the cache stopped
 * caching — and, worse, the Firebase RTDB SDK's own unguarded
 * `setItem("firebase:previous_websocket_failure")` threw before it opened the
 * websocket, so realtime was dead on that browser until site data was cleared.
 *
 * Only keys a reload can fetch again are ever evicted. Draft config
 * (`<org>_appConfig` …) and user app state (`<org>:<user>_appState`) can be
 * the only copy of someone's work, so they are never touched.
 *
 * Recency lives in one small index key. It is advisory: it is reconciled
 * against what is actually stored on first use each page load, so entries
 * written by an older bundle (or another tab) join it as least-recent.
 */

/**
 * Well under every engine's quota, leaving room for app state, drafts and the
 * SDKs' own keys. Measured in WebKit's charge (see `storageCost`), the tightest.
 */
export const CACHE_BUDGET_BYTES = 3 * 1024 * 1024;

export const CACHE_INDEX_KEY = "orgiggly-cache-lru";

/** Reads re-stamp recency at most this often, so a hot loop of `get`s doesn't rewrite the index each time. */
const TOUCH_INTERVAL_MS = 60_000;

export type StorageLike = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "key" | "length"
>;

/**
 * Entries a reload can fetch again: `<org>:published` (Site config),
 * `published:<org>:<version>` (versioned config) and
 * `orgiggly:<org>:data:…` (record batches).
 */
export const isEvictableCacheKey = (key: string): boolean =>
  /^[^:]+:published$/.test(key) ||
  key.startsWith("published:") ||
  /^orgiggly:.+:data:/.test(key);

const WIDE_CHAR = /[Ā-￿]/;

/**
 * WebKit charges 1 byte per char for a Latin-1 string and 2 for any other, so
 * one CJK character makes a whole batch (dave/hsk's Word) cost double.
 * Chromium and Firefox count chars against a larger effective quota.
 */
export const storageCost = (key: string, value: string): number =>
  [key, value].reduce(
    (sum, s) => sum + (WIDE_CHAR.test(s) ? s.length * 2 : s.length),
    0,
  );

type IndexEntry = { t: number; n: number };
type Index = Record<string, IndexEntry>;

const isIndexEntry = (v: unknown): v is IndexEntry =>
  typeof v === "object" &&
  v !== null &&
  "t" in v &&
  typeof v.t === "number" &&
  "n" in v &&
  typeof v.n === "number";

const parseIndex = (raw: string | null): Index => {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => isIndexEntry(v)),
    );
  } catch {
    return {};
  }
};

export type CacheBudget = {
  /**
   * Write `value`, evicting least-recently-used cache entries when the budget
   * or the browser's quota needs room. Works for ANY key — a non-cache writer
   * (app state) uses it to make room too. Returns false when the value could
   * not be stored even after evicting everything evictable. Never throws.
   */
  set: (key: string, value: string) => boolean;
  /** Mark a cache entry as just used. */
  touch: (key: string) => void;
  /** Drop keys the caller has removed from storage. */
  forget: (keys: string[]) => void;
  /** Evict down to the budget — run once at startup, before Firebase connects. */
  prune: () => void;
};

export const createCacheBudget = (
  storage: StorageLike,
  budget: number = CACHE_BUDGET_BYTES,
  now: () => number = Date.now,
): CacheBudget => {
  let index: Index | undefined;

  const load = (): Index => {
    if (index) return index;
    let stored: Index = {};
    try {
      stored = parseIndex(storage.getItem(CACHE_INDEX_KEY));
    } catch {
      // unreadable storage — start empty
    }
    const next: Index = {};
    try {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key || !isEvictableCacheKey(key)) continue;
        next[key] = stored[key] ?? {
          t: 0,
          n: storageCost(key, storage.getItem(key) ?? ""),
        };
      }
    } catch {
      // storage disabled — nothing to track
    }
    index = next;
    return next;
  };

  const save = () => {
    try {
      storage.setItem(CACHE_INDEX_KEY, JSON.stringify(index ?? {}));
    } catch {
      // advisory only — the next page load's reconcile rebuilds it
    }
  };

  const total = (idx: Index) =>
    Object.values(idx).reduce((sum, e) => sum + e.n, 0);

  const evictOne = (idx: Index, keep?: string): boolean => {
    let victim: string | undefined;
    for (const [key, entry] of Object.entries(idx)) {
      if (key === keep) continue;
      if (victim === undefined || entry.t < idx[victim].t) victim = key;
    }
    if (victim === undefined) return false;
    try {
      storage.removeItem(victim);
    } catch {
      // ignore — dropping it from the index is what matters
    }
    delete idx[victim];
    return true;
  };

  const set = (key: string, value: string): boolean => {
    const idx = load();
    const evictable = isEvictableCacheKey(key);
    if (evictable) {
      const n = storageCost(key, value);
      delete idx[key];
      if (n > budget) {
        // Keeping it would evict everything else and still crowd the origin.
        try {
          storage.removeItem(key);
        } catch {
          // ignore
        }
        save();
        return false;
      }
      while (total(idx) + n > budget && evictOne(idx, key));
    }
    for (;;) {
      try {
        storage.setItem(key, value);
        break;
      } catch {
        if (!evictOne(idx, key)) {
          save();
          return false;
        }
      }
    }
    if (evictable) idx[key] = { t: now(), n: storageCost(key, value) };
    save();
    return true;
  };

  const touch = (key: string) => {
    const idx = load();
    const entry = idx[key];
    if (!entry || now() - entry.t < TOUCH_INTERVAL_MS) return;
    entry.t = now();
    save();
  };

  const forget = (keys: string[]) => {
    const idx = load();
    const before = Object.keys(idx).length;
    keys.forEach((key) => delete idx[key]);
    if (Object.keys(idx).length !== before) save();
  };

  const prune = () => {
    const idx = load();
    while (total(idx) > budget && evictOne(idx));
    save();
  };

  return { set, touch, forget, prune };
};

let shared: CacheBudget | undefined;

/** The page's one budget over `window.localStorage` — every writer must share it. */
export const localStorageBudget = (): CacheBudget => {
  shared ??= createCacheBudget(localStorage);
  return shared;
};
